import { yupResolver } from '@hookform/resolvers/yup'
import { useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import SelectField from '@/components/select-field'
import { Button, Combobox, DateTimePicker, Input, Modal } from '@/components/ui'
import {
  coinVouchersBatch,
  CoinVouchersBatchPayload,
  coinVouchersEmailUpload,
} from '@/core/services/voucher'
import { InfoIcon } from 'lucide-react'
import { getTranslation } from '../../../account/services/auth'
import FormGroup from '../../../core/components/FormGroup'
import { formatDateTime } from '../../../core/constants/times'
import { IUser, searchUsersByEmail } from '../../../core/services/users'
import { MessageBox } from '../messages/MessageBox'

export interface IRedeemVoucherValues {
  name: string
  value: number
  quantity: number
  nominated: 'Yes' | 'No'
  wayToSend?: 'Email' | 'Download'
  receiverEmail?: string
  emailList?: 'Upload' | 'Manual'
  uploadKey?: string
  searchEmail?: string
  selectedUser?: IUser
  selectedUsers?: IUser[]
  usagePerVoucher?: 'Unlimited' | 'Limited'
  limitAmount?: number
  expiryDate: string
  canBeRedeemedUntil: string
}

export interface RedeemVoucherModalProps {
  showModal: boolean
  setShowModal: (s: boolean) => void
  onVoucherCreated?: () => void
  currency: string
}

export const RedeemVoucherModal = (
  props: RedeemVoucherModalProps,
): JSX.Element => {
  const { showModal, setShowModal, onVoucherCreated, currency } = props

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [correct, setCorrect] = useState<number>(0)
  const [faileds, setFaileds] = useState<string[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [usersData, setUsersData] = useState<{
    users: IUser[]
    isLoading: boolean
  }>({ users: [], isLoading: false })

  const language = getTranslation()

  const schema = yup.object().shape({
    name: yup.string().required('Name is required'),
    value: yup
      .number()
      .required('Value is required')
      .positive('Value must be positive'),
    quantity: yup
      .number()
      .required('Quantity is required')
      .positive('Quantity must be positive')
      .integer('Quantity must be an integer')
      .typeError('Quantity is required'),
    nominated: yup.mixed<'Yes' | 'No'>().oneOf(['Yes', 'No']).required(),
    wayToSend: yup
      .mixed<'Email' | 'Download'>()
      .oneOf(['Email', 'Download'])
      .when('nominated', {
        is: 'No',
        then: (schema) => schema.required(),
      }),
    receiverEmail: yup.string().when(['nominated', 'wayToSend'], {
      is: (nominated: string, wayToSend: string) =>
        nominated === 'No' && wayToSend === 'Email',
      then: (schema) => schema.email().required('Email is required'),
    }),
    emailList: yup
      .mixed<'Upload' | 'Manual'>()
      .oneOf(['Upload', 'Manual'])
      .when('nominated', {
        is: 'Yes',
        then: (schema) => schema.required(),
      }),
    searchEmail: yup.string().when(['nominated', 'emailList'], {
      is: (nominated: string, emailList: string) =>
        nominated === 'Yes' && emailList === 'Manual',
      then: (schema) => schema.required('Please select a user'),
    }),
    uploadKey: yup.string().when(['nominated', 'emailList'], {
      is: (nominated: string, emailList: string) =>
        nominated === 'Yes' && emailList === 'Upload',
      then: (schema) => schema.required('Please upload a CSV file'),
      otherwise: (schema) => schema.optional(),
    }),
    selectedUser: yup.mixed<IUser>().optional(),
    selectedUsers: yup.array().of(yup.mixed<IUser>().required()).optional(),
    usagePerVoucher: yup
      .mixed<'Unlimited' | 'Limited'>()
      .oneOf(['Unlimited', 'Limited'])
      .when('nominated', {
        is: 'Yes',
        then: (schema) => schema.required(),
      }),
    limitAmount: yup.number().when(['nominated', 'usagePerVoucher'], {
      is: (nominated: string, usagePerVoucher: string) =>
        nominated === 'Yes' && usagePerVoucher === 'Limited',
      then: (schema) =>
        schema
          .required('Limit amount is required')
          .positive('Must be positive')
          .integer('Must be an integer'),
      otherwise: (schema) =>
        schema.optional().transform((v) => (isNaN(v) ? undefined : v)),
    }),
    expiryDate: yup.string().required('Expiry date is required'),
    canBeRedeemedUntil: yup
      .string()
      .required('Can be redeemed until date is required'),
  })

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<IRedeemVoucherValues>({
    mode: 'onChange',
    resolver: yupResolver<IRedeemVoucherValues>(schema),
    defaultValues: {
      nominated: 'No',
      wayToSend: 'Email',
      emailList: 'Upload',
      usagePerVoucher: 'Unlimited',
    },
  })

  const watchNominated = watch('nominated')
  const watchWayToSend = watch('wayToSend')
  const watchEmailList = watch('emailList')
  const watchUsagePerVoucher = watch('usagePerVoucher')
  const watchExpiryDate = watch('expiryDate')
  const watchCanBeRedeemedUntil = watch('canBeRedeemedUntil')
  const watchSelectedUsers = watch('selectedUsers')

  const handleClose = () => {
    setShowModal(false)
    reset()
    setCorrect(0)
    setFaileds([])
    setSelectedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    setUsersData({ users: [], isLoading: false })
  }

  const handleAddUser = () => {
    const selectedUser = watch('selectedUser')
    const currentUsers = watch('selectedUsers') || []

    if (selectedUser && !currentUsers.find((u) => u.id === selectedUser.id)) {
      setValue('selectedUsers', [...currentUsers, selectedUser])
      setValue('selectedUser', undefined)
      setValue('searchEmail', '')
    }
  }

  const handleRemoveUser = (userId: string) => {
    const currentUsers = watch('selectedUsers') || []
    setValue(
      'selectedUsers',
      currentUsers.filter((u) => u.id !== userId),
    )
  }

  const handleSearch = async (query: string, signal: AbortSignal) => {
    setUsersData((prev) => ({
      ...prev,
      isLoading: true,
    }))

    try {
      const { data, status } = await searchUsersByEmail(query, signal)
      if (status === 200) {
        setUsersData({
          users: data,
          isLoading: false,
        })
      } else {
        setUsersData({
          users: [],
          isLoading: false,
        })
      }
    } catch (e) {
      if (signal.aborted) return
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
      setUsersData({
        users: [],
        isLoading: false,
      })
    }
  }

  const submit = async (values: IRedeemVoucherValues) => {
    try {
      setSubmitting(true)

      const payload: CoinVouchersBatchPayload = {
        label: values.name,
        locale: language.value,
        nrOfVouchers: values.quantity,
        amount: values.value,
        nominated: values.nominated === 'Yes',
        unlimited: values.usagePerVoucher === 'Unlimited',
        voucherLimit: values.limitAmount || 0,
        expirationDate: values.expiryDate
          ? new Date(values.expiryDate).valueOf()
          : 0,
        unPublishDate: values.canBeRedeemedUntil
          ? new Date(values.canBeRedeemedUntil).valueOf()
          : 0,
        nominateType: values.emailList === 'Upload' ? 'UPLOAD' : 'MANUAL',
        uploadKey: values.uploadKey || '',
        emails:
          values.emailList === 'Manual'
            ? watchSelectedUsers?.map((user) => user.email || '') || []
            : [],
        generateType: 'SEND_EMAIL',
        receiverEmail: values.receiverEmail || '',
      }

      const { data, status } = await coinVouchersBatch(payload)
      console.log(data, status)
      if (status === 200) {
        onVoucherCreated?.()
        setIsError(false)
        setShowMessageBox(true)
        handleClose()
        setMessage('Voucher redeemed successfully')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (!showModal) return <></>

  return (
    <>
      <Modal
        open={showModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Vouchers" subtitle="Add New Voucher" />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              form="redeem-voucher-form"
              type="submit"
              variant="primary"
              loading={submitting}
              disabled={submitting}
            >
              {submitting ? 'Submitting ...' : 'Add New'}
            </Button>
          </div>
        }
      >
        <form
          id="redeem-voucher-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="WINTER28 GIVEAWAY"
            />
          </FormGroup>

          <FormGroup
            label={`Value in ${currency}`}
            error={errors?.value?.message}
          >
            <Input
              isInvalid={!!errors?.value?.message}
              {...register('value')}
              type="number"
              placeholder="500"
            />
          </FormGroup>

          <FormGroup
            label="How many Vouchers to create?"
            error={errors?.quantity?.message}
          >
            <Input
              isInvalid={!!errors?.quantity?.message}
              {...register('quantity')}
              type="number"
              placeholder="1"
            />
          </FormGroup>

          <FormGroup
            label="Nominated or Not?"
            error={errors?.nominated?.message}
          >
            <Controller
              name="nominated"
              control={control}
              render={({ field }) => (
                <SelectField
                  outline
                  value={field.value}
                  onValueChange={field.onChange}
                  options={[
                    { label: 'Yes', value: 'Yes' },
                    { label: 'No', value: 'No' },
                  ]}
                  placeholder="Select"
                  error={errors?.nominated?.message}
                />
              )}
            />
          </FormGroup>

          {watchNominated === 'Yes' && (
            <>
              <FormGroup
                label="Usage per Voucher"
                error={errors?.usagePerVoucher?.message}
              >
                <Controller
                  name="usagePerVoucher"
                  control={control}
                  render={({ field }) => (
                    <SelectField
                      outline
                      value={field.value}
                      onValueChange={field.onChange}
                      options={[
                        { label: 'Unlimited', value: 'Unlimited' },
                        { label: 'Limited', value: 'Limited' },
                      ]}
                      placeholder="Select"
                      error={errors?.usagePerVoucher?.message}
                    />
                  )}
                />
              </FormGroup>

              {watchUsagePerVoucher === 'Limited' && (
                <FormGroup
                  label="Enter limit amount (min 1)"
                  error={errors?.limitAmount?.message}
                >
                  <Input
                    isInvalid={!!errors?.limitAmount?.message}
                    {...register('limitAmount')}
                    type="number"
                    placeholder="1"
                  />
                </FormGroup>
              )}

              <FormGroup label="Email List" error={errors?.emailList?.message}>
                <Controller
                  name="emailList"
                  control={control}
                  render={({ field }) => (
                    <SelectField
                      outline
                      value={field.value}
                      onValueChange={field.onChange}
                      options={[
                        { label: 'Upload', value: 'Upload' },
                        { label: 'Manual', value: 'Manual' },
                      ]}
                      placeholder="Select"
                      error={errors?.emailList?.message}
                    />
                  )}
                />
              </FormGroup>

              {watchEmailList === 'Upload' && (
                <>
                  <FormGroup
                    label="Upload Email Addresses (CSV)"
                    error={errors?.uploadKey?.message}
                  >
                    <div className="flex flex-col gap-2 w-full">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => {
                          const file = e?.target?.files?.[0]
                          if (file) setSelectedFile(file)
                        }}
                      />
                      <div className="flex items-center gap-2 w-full">
                        <Button
                          variant="secondary"
                          type="button"
                          className="flex items-center gap-2"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <span>
                            {selectedFile
                              ? selectedFile.name
                              : 'Choose CSV file'}
                          </span>
                        </Button>
                        <Button
                          variant="primary"
                          type="button"
                          loading={isUploading}
                          disabled={!selectedFile || isUploading}
                          onClick={async () => {
                            if (!selectedFile) return
                            setIsUploading(true)
                            setCorrect(0)
                            setFaileds([])
                            try {
                              const { data, status } =
                                await coinVouchersEmailUpload(selectedFile)
                              if (status === 200) {
                                setValue('uploadKey', data.uploadKey, {
                                  shouldValidate: true,
                                })
                                setValue(
                                  'quantity',
                                  data.successfullyValidated,
                                  { shouldValidate: true },
                                )
                                setCorrect(data.successfullyValidated)
                                setFaileds(data.failedEmails)
                              }
                            } catch (e) {
                              if (e instanceof Error) {
                                setIsError(true)
                                setShowMessageBox(true)
                                setMessage(e.message)
                                setErrorStatus(
                                  (e as { response?: { status?: number } })
                                    ?.response?.status,
                                )
                              } else {
                                console.error(e)
                              }
                            } finally {
                              setIsUploading(false)
                            }
                          }}
                        >
                          Upload
                        </Button>
                      </div>
                    </div>
                  </FormGroup>
                  <div className="flex flex-col gap-1">
                    {correct > 0 && (
                      <div className="p-3 flex flex-row items-center bg-green-100 border border-green-300 w-full rounded-lg">
                        <InfoIcon className="w-4 h-4 mr-2 text-green-800" />
                        <span className="text-sm text-green-800 w-full">
                          {`${correct} emails successfully validated`}
                        </span>
                      </div>
                    )}
                    {faileds.length > 0 &&
                      faileds.map((failed) => (
                        <div
                          key={failed}
                          className="p-3 flex flex-row items-center bg-red-100 border border-red-300 w-full rounded-lg"
                        >
                          <InfoIcon className="w-4 h-4 mr-2 text-red-800" />
                          <span className="text-sm text-red-800 w-full">
                            {`${failed} is not valid`}
                          </span>
                        </div>
                      ))}
                  </div>
                </>
              )}

              {watchEmailList === 'Manual' && (
                <>
                  <FormGroup
                    label="Select emails to send invitations"
                    error={errors?.searchEmail?.message}
                  >
                    <Controller
                      control={control}
                      name="searchEmail"
                      render={({ field }) => (
                        <Combobox
                          value={field.value || ''}
                          onValueChange={(value) => {
                            const selectedUser = usersData.users.find(
                              (u) => u.email === value,
                            )
                            if (selectedUser) {
                              setValue('searchEmail', selectedUser.email)
                              setValue('selectedUser', selectedUser)
                            }
                          }}
                          onSearch={handleSearch}
                          options={
                            usersData.users
                              ?.filter((user) => user.email)
                              .map((user) => ({
                                label:
                                  user.firstName +
                                  ' ' +
                                  user.lastName +
                                  ' (' +
                                  user.email +
                                  ')',
                                value: user.email!,
                              })) || []
                          }
                          isLoading={usersData.isLoading}
                          placeholder="Search user ..."
                          minSearchLength={3}
                          isInvalid={!!errors?.selectedUsers?.message}
                          shouldFilter={false}
                          renderOption={(option) => {
                            const user = usersData.users.find(
                              (u) => u.email === option.value,
                            )
                            return (
                              <div className="flex flex-col">
                                <span>{user?.email}</span>
                                <span className="text-monochrome-2 text-xs">
                                  {user?.firstName} {user?.lastName} (
                                  {user?.email})
                                </span>
                              </div>
                            )
                          }}
                        />
                      )}
                    />
                  </FormGroup>

                  {watchSelectedUsers && watchSelectedUsers.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-monochrome-1">
                        Selected Users ({watchSelectedUsers.length})
                      </label>
                      <div className="space-y-2">
                        {watchSelectedUsers.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between p-3 bg-monochrome-4 rounded-lg border border-monochrome-120"
                          >
                            <div className="flex flex-col">
                              <span className="text-sm text-monochrome-1">
                                {user.email}
                              </span>
                              <span className="text-xs text-monochrome-2">
                                {user.firstName} {user.lastName} ({user.phone})
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              onClick={() => handleRemoveUser(user.id!)}
                              className="!text-accent-3 hover:!bg-accent-3/10"
                            >
                              <Icon
                                icon="delete"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                              />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    type="button"
                    onClick={handleAddUser}
                  >
                    <span>Add New</span>
                    <Icon
                      icon="plus-circle"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                    />
                  </Button>
                </>
              )}
            </>
          )}

          {watchNominated === 'No' && (
            <>
              <FormGroup label="Way to Send" error={errors?.wayToSend?.message}>
                <Controller
                  name="wayToSend"
                  control={control}
                  render={({ field }) => (
                    <SelectField
                      outline
                      value={field.value}
                      onValueChange={field.onChange}
                      options={[
                        { label: 'Email', value: 'Email' },
                        { label: 'Download', value: 'Download' },
                      ]}
                      placeholder="Select"
                      error={errors?.wayToSend?.message}
                    />
                  )}
                />
              </FormGroup>

              {watchWayToSend === 'Email' && (
                <FormGroup
                  label="Receiver Email"
                  error={errors?.receiverEmail?.message}
                >
                  <Input
                    isInvalid={!!errors?.receiverEmail?.message}
                    {...register('receiverEmail')}
                    type="email"
                    placeholder="Enter Email"
                  />
                </FormGroup>
              )}
            </>
          )}

          <FormGroup
            label="Coins Expiry Date & Time"
            error={errors?.expiryDate?.message}
          >
            <DateTimePicker
              hasError={!!errors?.expiryDate?.message}
              value={watchExpiryDate ? new Date(watchExpiryDate) : undefined}
              onChange={(date) => {
                setValue(
                  'expiryDate',
                  date ? formatDateTime(date.valueOf()) : '',
                  {
                    shouldValidate: true,
                  },
                )
              }}
              placeholder="2028-02-08 12:00"
            />
          </FormGroup>

          <FormGroup
            label="Can be Redeemed Until"
            error={errors?.canBeRedeemedUntil?.message}
          >
            <DateTimePicker
              hasError={!!errors?.canBeRedeemedUntil?.message}
              value={
                watchCanBeRedeemedUntil
                  ? new Date(watchCanBeRedeemedUntil)
                  : undefined
              }
              onChange={(date) => {
                setValue(
                  'canBeRedeemedUntil',
                  date ? formatDateTime(date.valueOf()) : '',
                  {
                    shouldValidate: true,
                  },
                )
              }}
              placeholder="2028-02-09 12:00"
            />
          </FormGroup>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
